import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { PageTitle } from '../../../_metronic/layout/core';
import {
  ShopifyOrder,
  ShopifyOrderList,
  ShopifyOrderStateEnum,
  BulkActionsEnum,
  listShopifyOrders,
  performBulkAction,
} from '../../modules/shopify';
import DateRangeSelector from '../../components/DateRangeSelector';
import moment from 'moment';
import { listShippingServices } from '../../modules/plans';
import { Warehouse } from '../../modules/stock/models/StockLine';
import WarehouseFilter from '../../components/WarehouseFilter';

const ANY_STATE = 'ANY_STATE';
const DEBOUNCE_TIMEOUT = 700;

const ShopifyOrdersPage: FC = () => {
  const intl = useIntl();
  const [orders, setOrders] = useState<Array<ShopifyOrder>>([]);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<ShopifyOrderStateEnum | null>(
    ShopifyOrderStateEnum.LOCKED
  );
  const [since, setSince] = useState<string | null>(null);
  const [until, setUntil] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const initialSince = moment().add(1, 'days').toDate();
  const initialUntil = moment().add(7, 'days').toDate();
  const [shippingServices, setShippingServices] = useState([]);
  const [selectedShippingService, setSelectedShippingService] = useState(null);
  const [reference, setReference] = useState<string>('');
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);

  useEffect(() => {
    if (!shippingServices?.length) {
      fetchShippingServices();
    }
  }, []);

  async function fetchShippingServices() {
    try {
      const { data: shippingServices } = await listShippingServices();
      setShippingServices(shippingServices);
    } catch (error) {
      console.warn({ error });
    }
  }

  function onPageChange(page: number) {
    setPage(page);
  }

  async function fetchOrders(withLoader = true) {
    try {
      if (withLoader) {
        setLoading(true);
      }
      const { data } = await listShopifyOrders({
        state: state,
        since,
        until,
        page,
        shippingService: selectedShippingService,
        reference,
        warehouse,
      });
      setOrders(data.results);
      setCount(data.count);
    } catch (error) {
      console.warn(error);
    } finally {
      if (withLoader) {
        setLoading(false);
      }
    }
  }

  const fetchDependencies = [
    state,
    since,
    until,
    selectedShippingService,
    reference,
    warehouse,
  ];
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (since && until && page > -1) {
        fetchOrders();
      }
    }, DEBOUNCE_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [...fetchDependencies, page]);

  useEffect(() => {
    setPage(1);
  }, fetchDependencies);

  function onStateChange(event: React.ChangeEvent<HTMLSelectElement>) {
    if (event.target.value === ANY_STATE) {
      setState(null);
    } else {
      setState(event.target.value as ShopifyOrderStateEnum);
    }
  }

  useEffect(() => {
    onAcceptDateRange(initialSince, initialUntil);
  }, []);

  async function onPerformBulkAction(
    action: BulkActionsEnum,
    orders: number[]
  ) {
    return performBulkAction({
      action,
      orders,
      promise_date__gte: since,
      promise_date__lte: until,
      shippingService: selectedShippingService,
      state,
      reference,
      warehouse,
    });
  }

  function onAcceptDateRange(since: Date, until: Date) {
    setSince(
      `${since.getFullYear()}-${(since.getMonth() + 1).toString().padStart(2, '0')}-${since.getDate().toString().padStart(2, '0')}`
    );
    setUntil(
      `${until.getFullYear()}-${(until.getMonth() + 1).toString().padStart(2, '0')}-${until.getDate().toString().padStart(2, '0')}`
    );
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'shopifyOrders.title' })}
      </PageTitle>
      <div className="row">
        <div className="col-12">
          <div className="card p-10">
            <ShopifyOrderList
              orders={orders}
              loading={loading}
              page={page}
              count={count}
              onPageChange={onPageChange}
              onRefresh={fetchOrders}
              onBulkGenerateWarehouseOperationsSheet={(orders) =>
                onPerformBulkAction(
                  BulkActionsEnum.GENERATE_WAREHOUSE_OPERATIONS_SHEET,
                  orders
                )
              }
              onBulkGenerateProductsNecessitySheet={(orders) =>
                onPerformBulkAction(
                  BulkActionsEnum.GENERATE_PRODUCTS_NECESSITY_SHEET,
                  orders
                )
              }
              onBulkPrepareOrdersLegacy={(orders) =>
                onPerformBulkAction(
                  BulkActionsEnum.PREPARE_ORDERS_LEGACY,
                  orders
                )
              }
              onBulkPrepareOrders={(orders) =>
                onPerformBulkAction(BulkActionsEnum.PREPARE_ORDERS, orders)
              }
            >
              <div className="d-flex flex-wrap gap-4">
                <div className="d-inline-block">
                  <DateRangeSelector
                    initialSince={initialSince}
                    initialUntil={initialUntil}
                    onSelected={onAcceptDateRange}
                    disableFutureDates={false}
                  />
                </div>
                <div className="form-group w-180px d-inline-block">
                  <label className="form-label">
                    {intl.formatMessage({ id: 'shopifyOrders.filterByState' })}
                  </label>
                  <select
                    className="form-control form-select"
                    value={state}
                    onChange={onStateChange}
                  >
                    <option value={ANY_STATE}>
                      {intl.formatMessage({ id: 'shopifyOrders.anyState' })}
                    </option>
                    {Object.values(ShopifyOrderStateEnum).map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group w-180px d-inline-block">
                  <label className="form-label">
                    {intl.formatMessage({
                      id: 'shopifyOrders.filterShippingService',
                    })}
                  </label>
                  <select
                    className="form-control form-select"
                    value={selectedShippingService}
                    onChange={(e) => {
                      if (e.target.value === ANY_STATE) {
                        setSelectedShippingService(null);
                      } else {
                        setSelectedShippingService(e.target.value);
                      }
                    }}
                  >
                    <option value={ANY_STATE}>
                      {intl.formatMessage({
                        id: 'shopifyOrders.anyShippingService',
                      })}
                    </option>
                    {shippingServices.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group w-180px d-inline-block">
                  <label className="form-label">
                    {intl.formatMessage({
                      id: 'shopifyOrders.filterByReference',
                    })}
                  </label>
                  <input
                    placeholder={intl.formatMessage({
                      id: 'shopifyOrders.referencePlaceholder',
                    })}
                    type="text"
                    className="form-control"
                    value={reference}
                    onChange={(e) =>
                      setReference(e.target.value?.trim()?.replace('#', ''))
                    }
                  />
                </div>
                <WarehouseFilter value={warehouse} onChange={setWarehouse} />
              </div>
            </ShopifyOrderList>
          </div>
        </div>
      </div>
    </>
  );
};

export { ShopifyOrdersPage };
